import { createPortal } from "react-dom";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { MapPin, Check, X } from "lucide-react";
import { FaLocationCrosshairs } from "react-icons/fa6";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PlacesAutocomplete } from "../admin/common/form-elements";

export const getCurrentLocation = async () => {
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

  const getBrowserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy, // meters
            source: "browser",
          });
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const getGoogleLocation = async () => {
    try {
      const { data } = await axios.post(
        `https://www.googleapis.com/geolocation/v1/geolocate?key=${GOOGLE_API_KEY}`
      );

      return {
        lat: data.location.lat,
        lng: data.location.lng,
        accuracy: data.accuracy,
        source: "google",
      };
    } catch (error) {
      console.error("Google Geolocation API failed:", error);
      throw new Error("Google Geolocation failed");
    }
  };

  try {
    const browserLoc = await getBrowserLocation();

    // If browser accuracy is good enough (<= 5m), trust it
    if (browserLoc.accuracy && browserLoc.accuracy <= 5) {
      console.log("📍 Using browser location:", browserLoc);
      return browserLoc;
    }

    // Otherwise fallback to Google API for network triangulation
    console.warn(
      `⚠️ Browser accuracy too low (${browserLoc.accuracy}m), using Google fallback`
    );
    const googleLoc = await getGoogleLocation();

    return googleLoc;
  } catch (browserError) {
    console.warn("Browser geolocation failed, using Google fallback");
    try {
      const googleLoc = await getGoogleLocation();
      return googleLoc;
    } catch (googleError) {
      throw new Error("Both browser and Google geolocation failed");
    }
  }
};

const MapModal = ({ isOpen, onClose, onSelect, initialLocation }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
  });

  const mapRef = useRef(null);
  const [marker, setMarker] = useState(initialLocation);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    const fetchMyLocation = async () => {
      try {
        const loc = await getCurrentLocation();
        // console.log("location :", loc);
        setMarker({ lat: loc.lat, lng: loc.lng });

        // Center the map after it loads
        if (mapRef.current) {
          mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
        }
      } catch {
        console.log("User denied location or unavailable.");
      }
    };

    if (!initialLocation) {
      fetchMyLocation();
    }
  }, [initialLocation]);

  const getPart = (components, types) => {
    const tset = Array.isArray(types) ? types : [types];
    const comp = components.find((c) => tset.every((t) => c.types.includes(t)));
    return comp?.long_name || "";
  };

  const parseAddressComponents = (components) => {
    const streetNumber = getPart(components, "street_number");
    const route = getPart(components, "route");
    const street = [streetNumber, route].filter(Boolean).join(" ").trim();

    const locality =
      getPart(components, "sublocality") ||
      getPart(components, "sublocality_level_1") ||
      getPart(components, "neighborhood") ||
      "";

    const city =
      getPart(components, "locality") || // most reliable for city
      getPart(components, "administrative_area_level_3") ||
      getPart(components, "administrative_area_level_2") || // district (fallback)
      "";

    const state = getPart(components, "administrative_area_level_1");
    const country = getPart(components, "country") || "India";
    const pincode = getPart(components, "postal_code");

    return { street, locality, city, state, country, pincode };
  };

  const checkGeoPermission = async () => {
    if (!navigator.permissions) return null; // not supported on Safari <15
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state; // "granted", "prompt", or "denied"
  };

  // Preferred: Browser Geocoder (no extra fetch)
  const geocodeWithBrowserSDK = ({ lat, lng }) =>
    new Promise((resolve, reject) => {
      if (!window.google?.maps?.Geocoder) {
        return reject(new Error("Geocoder not available"));
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length) {
          resolve(results[0]);
        } else {
          reject(new Error(`Geocoder status: ${status}`));
        }
      });
    });

  // Fallback: REST API
  const geocodeWithREST = async ({ lat, lng }) => {
    const { data } = await axios.get(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        params: {
          latlng: `${lat},${lng}`,
          key: process.env.REACT_APP_GOOGLE_MAP_KEY,
        },
      }
    );
    if (data.status === "OK" && data.results?.length) return data.results[0];
    throw new Error(
      `REST Geocode status: ${data.status} ${data.error_message || ""}`
    );
  };

  const reverseGeocode = async ({ lat, lng }) => {
    try {
      // Try browser SDK first
      const result = await geocodeWithBrowserSDK({ lat, lng });
      // console.log("result:", result);
      const parsed = parseAddressComponents(result.address_components || []);
      return { formatted: result.formatted_address, ...parsed };
    } catch (e1) {
      console.warn("Browser geocoder failed, trying REST:", e1?.message);
      try {
        const result = await geocodeWithREST({ lat, lng });
        const parsed = parseAddressComponents(result.address_components || []);
        return { formatted: result.formatted_address, ...parsed };
      } catch (e2) {
        console.error("Reverse geocode failed:", e2?.message);
        return null;
      }
    }
  };

  const handleConfirm = async () => {
    if (!marker) return;
    setIsLoading(true);
    try {
      // console.log("cordinates:", marker);
      const addressData = await reverseGeocode(marker);
      // console.log("addressData:", addressData);
      if (!addressData) {
        alert(
          "Could not fetch address for this location. Try zooming in or tapping a nearby road."
        );
        return;
      }
      onSelect(marker, addressData);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseMyLocation = async () => {
    const state = await checkGeoPermission();

    if (state === "denied") {
      alert(
        "You've previously blocked location access. To allow it again:\n\n1️⃣ Click the lock icon near your address bar.\n2️⃣ Choose 'Site settings'.\n3️⃣ Under 'Location', set to 'Allow'.\n4️⃣ Then refresh this page."
      );
      return;
    }

    try {
      setIsLocating(true);
      const loc = await getCurrentLocation();
      setMarker(loc);
      mapRef.current?.panTo(loc);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLocating(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center md:p-4">
      <div className="bg-white md:rounded-xl shadow-2xl w-full md:max-w-4xl h-screen md:h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 border-b border-border bg-primary-navy text-white flex items-center justify-between">
          {/* Title */}
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-white/90" />
            <h3 className="text-sm sm:text-base font-medium">
              Select Location on Map
            </h3>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/10 active:scale-[.95] transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative modal-map-container">
          {isLoaded && (
            <div className="absolute top-2 md:top-4 left-1/2 -translate-x-1/2 w-[90%] sm:w-[60%] z-[30]">
              <PlacesAutocomplete
                onSelect={(place) => {
                  const coords = {
                    lat: place.location.latitude,
                    lng: place.location.longitude,
                  };
                  setMarker(coords);
                  mapRef.current?.panTo(coords);
                }}
              />
            </div>
          )}

          {/* Google Map */}
          <GoogleMap
            onLoad={(map) => (mapRef.current = map)}
            zoom={marker ? 15 : 12}
            onClick={(e) =>
              setMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() })
            }
            center={marker || { lat: 19.2183, lng: 72.9781 }}
            mapContainerClassName="w-full h-full"
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: false,
            }}
          >
            {marker && (
              <Marker
                position={marker}
                animation={window.google?.maps?.Animation?.DROP}
              />
            )}
          </GoogleMap>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-border bg-background flex flex-col sm:flex-row items-center justify-between gap-3">
          {marker && (
            <div className="hidden sm:flex items-center gap-2 text-xs text-text-secondary">
              <Check className="w-4 h-4 text-status-success" />
              {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)}
            </div>
          )}

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {/* Cancel */}
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium rounded-md border border-border text-text-secondary hover:bg-gray-50 active:scale-[.97] transition"
            >
              Cancel
            </button>

            {/* Use My Location */}
            <button
              onClick={handleUseMyLocation}
              disabled={isLocating}
              className="px-4 py-2 text-xs font-medium rounded-md border border-primary text-primary hover:bg-primary/10 active:scale-[.97] disabled:opacity-60 transition flex items-center gap-1"
            >
              {isLocating ? (
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
              ) : (
                <div>
                  <FaLocationCrosshairs className="w-4 h-4" />
                </div>
              )}
              <span className="hidden md:block">My Location</span>
            </button>

            {/* Confirm */}
            <button
              onClick={handleConfirm}
              disabled={!marker || isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-md text-white bg-primary hover:bg-primary/90 active:scale-[.97] transition disabled:opacity-50 flex items-center gap-1"
            >
              {isLoading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              <span>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
export default MapModal;
